import React from "react";

/**
 * UploadProgress — the one upload indicator, shared by every upload surface.
 * ────────────────────────────────────────────────────────────────────────────
 * WHY THIS EXISTS
 * Six screens had each grown their own copy of
 * `<ProgressBar now={pct} label={`${pct}%`} />`, and half of them printed the
 * same percentage a second time in a caption underneath. They all rendered
 * badly for one reason: the admin theme slims Bootstrap's `.progress` to a few
 * pixels, and a 6px track cannot contain a 12px label — so the percentage sat
 * clipped across the middle of the bar instead of inside it.
 *
 * The track here is plain divs with explicit heights. It does not touch
 * `.progress`, so no theme rule can slim it out from under the label again, and
 * the percentage lives in its own row above the bar where it has room.
 *
 * THE 100% PROBLEM
 * `onProgress` reports bytes leaving the browser, not work finished. On a good
 * connection the bar reaches 100% and then sits there for several seconds while
 * Cloudinary stores the file and the API writes the record — which reads as a
 * hang, and is exactly what the "Uploading… 100%" screenshot was. At 100% this
 * switches to an indeterminate sweep and says "Finishing up", which is both
 * honest about what is happening and visibly still alive.
 *
 * USAGE
 *   {uploading && <UploadProgress percent={uploadProgress} label="Uploading image" />}
 *
 * The caller owns the `uploading` flag — render nothing when idle rather than
 * asking this component to hide itself, so the layout collapses cleanly.
 */

const TRACK_COLOR = "#e2e8f0";
const FILL_COLOR = "#2563eb";

const UploadProgress = ({
  /** 0-100. Values outside the range are clamped rather than trusted. */
  percent = 0,
  /** What is happening, without trailing punctuation — "Uploading image". */
  label = "Uploading",
  /** Shown once the bytes are all sent but the request has not returned. */
  settlingLabel = "Finishing up",
  className = "",
  style = {},
}) => {
  const pct = Math.min(100, Math.max(0, Math.round(Number(percent) || 0)));
  const settling = pct >= 100;

  return (
    <div
      className={`lsk-upload-progress ${className}`.trim()}
      style={{ marginTop: 12, ...style }}
    >
      <style>{`
        @keyframes lskUploadSweep {
          0%   { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        .lsk-upload-progress__sweep {
          position: absolute;
          top: 0; right: 0; bottom: 0; left: 0;
          background: linear-gradient(
            90deg,
            rgba(255, 255, 255, 0) 0%,
            rgba(255, 255, 255, 0.55) 50%,
            rgba(255, 255, 255, 0) 100%
          );
          animation: lskUploadSweep 1.1s ease-in-out infinite;
        }
        @media (prefers-reduced-motion: reduce) {
          .lsk-upload-progress__sweep { animation: none; }
        }
      `}</style>

      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          gap: 12,
          marginBottom: 6,
        }}
      >
        <span
          style={{ fontSize: "0.82rem", fontWeight: 600, color: "#334155" }}
        >
          {settling ? settlingLabel : label}…
        </span>
        <span
          style={{
            fontSize: "0.78rem",
            fontWeight: 700,
            color: "#64748b",
            // Tabular figures stop the number jittering as it counts up.
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {pct}%
        </span>
      </div>

      <div
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        // Omitted while settling: an indeterminate bar has no meaningful value,
        // and 100% would tell a screen reader the work is done when it isn't.
        aria-valuenow={settling ? undefined : pct}
        aria-valuetext={settling ? settlingLabel : `${pct}%`}
        aria-label={label}
        style={{
          position: "relative",
          height: 8,
          borderRadius: 999,
          background: TRACK_COLOR,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            bottom: 0,
            width: `${pct}%`,
            borderRadius: 999,
            background: FILL_COLOR,
            transition: "width 0.25s ease",
          }}
        >
          {settling ? <span className="lsk-upload-progress__sweep" /> : null}
        </div>
      </div>
    </div>
  );
};

export default UploadProgress;
